<template>
  <div class="websocket-status" v-if="showStatus">
    <!-- 🚀 主状态指示器 -->
    <div
      class="status-indicator"
      :class="{
        connected: chatStore.wsConnected,
        connecting: chatStore.wsConnecting,
        disconnected: !chatStore.wsConnected && !chatStore.wsConnecting,
        countdown: reconnectCountdown > 0
      }"
      @click="toggleDetails"
    >
      <div class="status-dot"></div>
      <div class="status-content">
        <span class="status-text">{{ statusText }}</span>
        <span v-if="errorLogs.length > 0" class="error-count">{{ errorLogs.length }}条日志</span>
      </div>
      <!-- 🚀 手动重连按钮 -->
      <button
        v-if="!chatStore.wsConnected && !chatStore.wsConnecting"
        @click.stop="manualReconnect"
        class="reconnect-btn"
      >
        重连
      </button>
    </div>

    <!-- 🚀 重连进度条 -->
    <div v-if="reconnectCountdown > 0 && !chatStore.wsConnected" class="countdown-bar">
      <div class="countdown-progress" :style="{ width: countdownProgress + '%' }"></div>
    </div>

    <div v-if="chatStore.wsError" class="error-message">
      {{ chatStore.wsError }}
    </div>

    <!-- 🔧 新增：错误日志详情（可展开） -->
    <div v-if="showDetails && errorLogs.length > 0" class="error-logs">
      <div class="logs-header">
        <span>错误日志 (最近 {{ errorLogs.length }} 条)</span>
        <button @click="clearLogs" class="clear-btn">清除</button>
      </div>
      <div class="logs-content">
        <div
          v-for="(log, index) in errorLogs.slice().reverse()"
          :key="index"
          class="log-item"
          :class="log.type"
        >
          <div class="log-header">
            <span class="log-type">{{ log.type }}</span>
            <span class="log-time">{{ formatTime(log.timestamp) }}</span>
          </div>
          <div class="log-info">
            <pre>{{ JSON.stringify(log.info, null, 2) }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useChatStore } from '@/stores/chat'
import { getWebSocketManager } from '@/utils/websocket'

interface Props {
  showStatus?: boolean
}

withDefaults(defineProps<Props>(), {
  showStatus: true
})

const chatStore = useChatStore()
const showDetails = ref(false)
const errorLogs = ref<any[]>([])

// 🚀 增强：添加重连倒计时
const reconnectCountdown = ref(0)
let countdownTimer: NodeJS.Timeout | null = null

const statusText = computed(() => {
  // 默认文案
  if (chatStore.wsConnected) return '✅ 已连接'

  // 尝试从 WebSocket 管理器中读取更详细的状态（重连次数、错误信息等）
  let reconnectCount = 0
  let lastError: string | null = chatStore.wsError
  try {
    const wsManager = getWebSocketManager()
    const status = wsManager.getStatus()
    reconnectCount = status.reconnectCount || 0
    if (!lastError && status.connectionError) {
      lastError = status.connectionError
    }
  } catch (_) {
    // 忽略状态读取错误，使用基础文案
  }

  // 正在连接阶段：区分"首次连接"与"重新连接"
  if (chatStore.wsConnecting) {
    if (reconnectCount > 0) {
      return `🔄 正在重连... (第 ${reconnectCount}/10 次)`
    }
    return '🔄 正在连接...'
  }

  // 🚀 显示重连倒计时
  if (reconnectCountdown.value > 0) {
    return `⏰ ${reconnectCountdown.value}秒后重连 (${reconnectCount}/10)`
  }

  // 已经断开：如果有错误信息，提示用户点击查看详情
  if (lastError) {
    return `❌ 连接失败 (${reconnectCount}/10)`
  }

  // 兜底文案
  return `⚠️ 未连接 (${reconnectCount}/10)`
})

const toggleDetails = () => {
  showDetails.value = !showDetails.value
  if (showDetails.value) {
    loadErrorLogs()
  }
}

const loadErrorLogs = () => {
  try {
    const wsManager = getWebSocketManager()
    errorLogs.value = wsManager.getErrorLogs()
  } catch (e) {
    console.error('加载错误日志失败:', e)
  }
}

const clearLogs = () => {
  try {
    const wsManager = getWebSocketManager()
    wsManager.clearErrorLogs()
    errorLogs.value = []
  } catch (e) {
    console.error('清除错误日志失败:', e)
  }
}

// 🚀 手动触发重连
const manualReconnect = () => {
  try {
    const wsManager = getWebSocketManager()
    console.log('🔄 用户手动触发重连')
    chatStore.wsConnecting = true
    wsManager.reconnectWithNewToken()
  } catch (e) {
    console.error('手动重连失败:', e)
  }
}

const formatTime = (timestamp: string) => {
  try {
    const date = new Date(timestamp)
    return date.toLocaleTimeString('zh-CN', { hour12: false })
  } catch {
    return timestamp
  }
}

// 🚀 监听重连倒计时
// 🚀 重连倒计时总时长
const maxCountdown = ref(0)

const startCountdown = () => {
  try {
    const wsManager = getWebSocketManager()
    const status = wsManager.getStatus()

    // 🔧 iOS 优化：计算倒计时（与 websocket.ts 的重连策略保持一致）
    if (!status.isConnected && !status.isConnecting && status.reconnectCount > 0) {
      let delay = 1000 // 默认1秒

      // 检查网络状态
      const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true

      if (!isOnline) {
        // 离线状态：5秒
        delay = 5000
      } else if (status.reconnectCount <= 3) {
        // 前3次：1秒快速重连
        delay = 1000
      } else if (status.reconnectCount <= 10) {
        // 4-10次：指数退避 1.5x，最大10秒
        let currentDelay = 1000
        for (let i = 4; i <= status.reconnectCount; i++) {
          currentDelay = Math.min(currentDelay * 1.5, 10000)
        }
        delay = currentDelay
      } else {
        // 10次以上：固定10秒
        delay = 10000
      }

      const seconds = Math.ceil(delay / 1000)

      // 只在倒计时重新开始时更新
      if (reconnectCountdown.value === 0 || reconnectCountdown.value > seconds) {
        reconnectCountdown.value = seconds
        maxCountdown.value = seconds

        if (countdownTimer) clearInterval(countdownTimer)
        countdownTimer = setInterval(() => {
          reconnectCountdown.value--
          if (reconnectCountdown.value <= 0) {
            if (countdownTimer) clearInterval(countdownTimer)
          }
        }, 1000)
      }
    } else {
      reconnectCountdown.value = 0
      maxCountdown.value = 0
      if (countdownTimer) clearInterval(countdownTimer)
    }
  } catch (e) {
    console.error('倒计时更新失败:', e)
  }
}

// 🚀 进度条百分比
const countdownProgress = computed(() => {
  if (maxCountdown.value === 0) return 0
  return ((maxCountdown.value - reconnectCountdown.value) / maxCountdown.value) * 100
})

// 定期刷新错误日志和倒计时
let refreshTimer: NodeJS.Timeout | null = null
onMounted(() => {
  refreshTimer = setInterval(() => {
    if (showDetails.value) {
      loadErrorLogs()
    }
    startCountdown()
  }, 500) // 每0.5秒刷新一次，让倒计时更流畅
})

onUnmounted(() => {
  if (refreshTimer) {
    clearInterval(refreshTimer)
  }
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
})
</script>

<style scoped>
.websocket-status {
  position: fixed;
  top: 50px;
  left: 110px;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  max-width: 400px;
  max-height: 80vh;
  overflow-y: auto;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  /* 🚀 iOS优化 */
  -webkit-overflow-scrolling: touch;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  padding: 2px 0;
}

.status-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.status-text {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.reconnect-btn {
  padding: 4px 12px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
}

.reconnect-btn:active {
  transform: scale(0.95);
  box-shadow: 0 1px 2px rgba(59, 130, 246, 0.3);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #666;
  transition: background-color 0.3s;
  flex-shrink: 0;
}

.connected .status-dot {
  background: #4ade80;
  box-shadow: 0 0 8px rgba(74, 222, 128, 0.5);
}

.connecting .status-dot {
  background: #fbbf24;
  animation: pulse 1.5s infinite;
}

.disconnected .status-dot {
  background: #ef4444;
}

.countdown .status-dot {
  background: #fb923c;
  animation: countdown-pulse 1s infinite;
}

/* 🚀 倒计时进度条 */
.countdown-bar {
  margin-top: 8px;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.countdown-progress {
  height: 100%;
  background: linear-gradient(90deg, #fbbf24, #fb923c);
  transition: width 0.5s linear;
  border-radius: 2px;
}

.error-count {
  color: #fca5a5;
  font-size: 10px;
  opacity: 0.8;
}

.error-message {
  margin-top: 4px;
  color: #fca5a5;
  font-size: 11px;
  word-break: break-word;
}

.error-logs {
  margin-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding-top: 8px;
}

.logs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 11px;
  color: #ccc;
}

.clear-btn {
  background: rgba(239, 68, 68, 0.3);
  border: 1px solid rgba(239, 68, 68, 0.5);
  color: #fca5a5;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  cursor: pointer;
}

.clear-btn:hover {
  background: rgba(239, 68, 68, 0.5);
}

.logs-content {
  max-height: 300px;
  overflow-y: auto;
}

.log-item {
  margin-bottom: 8px;
  padding: 6px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  border-left: 3px solid #666;
}

.log-item.error {
  border-left-color: #ef4444;
}

.log-item.close {
  border-left-color: #fbbf24;
}

.log-item.connect {
  border-left-color: #3b82f6;
}

.log-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 10px;
}

.log-type {
  font-weight: bold;
  text-transform: uppercase;
}

.log-time {
  color: #999;
}

.log-info {
  font-size: 10px;
  color: #ccc;
  max-height: 100px;
  overflow-y: auto;
}

.log-info pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: 'Courier New', monospace;
  font-size: 9px;
  line-height: 1.4;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes countdown-pulse {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.2);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
